/**
 * Editor "play preview" — replay a block's entrance motion in the canvas.
 *
 * Several blocks animate once, when the reader first scrolls them into view: a
 * bar fills, a number counts up, a chart draws itself, timeline entries fade
 * in. None of that can happen in the editor — the Interactivity API does not
 * run there, and even if it did the block is already on screen — so the canvas
 * shows the resting state and the author has to publish to find out what the
 * motion looks like.
 *
 * This is the missing button. It does not simulate the animation: it triggers
 * the block's real frontend class (`is-filling`, `is-revealing`, …) so what
 * plays in the canvas is the stylesheet the visitor will get. Blocks whose
 * motion is not CSS — the counter's easing runs in JavaScript — pass an
 * `onProgress` callback and drive their own value from the same clock.
 *
 * Playing is always an explicit press. These are one-shot reveals, not loops
 * like the marquee, and re-running them on every keystroke would make the
 * editor unusable while the author is typing a label.
 */

import { BlockControls } from '@wordpress/block-editor';
import { ToolbarButton, ToolbarGroup } from '@wordpress/components';
import { useCallback, useEffect, useRef, useState } from '@wordpress/element';
import { __ } from '@wordpress/i18n';

/**
 * Run a block's entrance motion once.
 *
 * @param {number}    duration   How long the motion lasts, in milliseconds.
 *                               Pass the block's own duration so the button
 *                               releases when the animation actually ends.
 * @param {Function} [onProgress] Optional per-frame callback receiving eased
 *                               time from 0 to 1. Supply it only for motion
 *                               that JavaScript drives; CSS-driven blocks want
 *                               the cheaper timer.
 * @return {{isPlaying: boolean, play: Function}} Playing flag and trigger.
 */
export function useReplayPreview(duration, onProgress) {
  const [isPlaying, setIsPlaying] = useState(false);
  const frameRef = useRef(0);
  const timerRef = useRef(0);

  // Read through a ref so a caller can pass an inline arrow without the
  // identity change re-creating `play` — and invalidating it mid-flight.
  const progressRef = useRef(onProgress);
  progressRef.current = onProgress;

  const stop = useCallback(() => {
    window.cancelAnimationFrame(frameRef.current);
    window.clearTimeout(timerRef.current);
  }, []);

  // A block removed from the canvas mid-play must not keep a timer alive that
  // will call setState on an unmounted component.
  useEffect(() => stop, [stop]);

  const play = useCallback(() => {
    stop();

    /*
     * Dropping the class and adding it back inside a single frame is no change
     * at all as far as the browser is concerned, so a second press would leave
     * the animation exactly where the first one finished. Clearing it, letting
     * one frame commit, then setting it again is what makes replay work.
     */
    setIsPlaying(false);

    frameRef.current = window.requestAnimationFrame(() => {
      setIsPlaying(true);

      if (!progressRef.current) {
        timerRef.current = window.setTimeout(
          () => setIsPlaying(false),
          duration
        );
        return;
      }

      const startedAt = performance.now();

      const tick = (now) => {
        const progress = Math.min((now - startedAt) / duration, 1);
        progressRef.current(progress);

        if (progress < 1) {
          frameRef.current = window.requestAnimationFrame(tick);
          return;
        }

        setIsPlaying(false);
      };

      frameRef.current = window.requestAnimationFrame(tick);
    });
  }, [duration, stop]);

  return { isPlaying, play };
}

/**
 * The toolbar button that goes with the hook.
 *
 * @param {Object}   props
 * @param {boolean}  props.isPlaying Whether the motion is running.
 * @param {Function} props.onPlay    Trigger returned by useReplayPreview.
 * @param {string}   [props.label]   Overrides the default tooltip.
 * @return {Element} Block toolbar control.
 */
export function PlayPreviewControl({ isPlaying, onPlay, label }) {
  return (
    <BlockControls>
      <ToolbarGroup>
        <ToolbarButton
          icon="controls-play"
          label={label || __('Play preview', 'brandy-blocks')}
          onClick={onPlay}
          isPressed={isPlaying}
        />
      </ToolbarGroup>
    </BlockControls>
  );
}
